import type { CurrencyCode } from "@majourneys/shared";

/**
 * Eurozone ISO-3166 alpha-2 country codes. Visitors from these countries get
 * prices in EUR by default. Edit this list to tune the mapping.
 */
export const EUROZONE_COUNTRIES: ReadonlySet<string> = new Set([
  "AT", // Austria
  "BE", // Belgium
  "HR", // Croatia
  "CY", // Cyprus
  "EE", // Estonia
  "FI", // Finland
  "FR", // France
  "DE", // Germany
  "GR", // Greece
  "IE", // Ireland
  "IT", // Italy
  "LV", // Latvia
  "LT", // Lithuania
  "LU", // Luxembourg
  "MT", // Malta
  "NL", // Netherlands
  "PT", // Portugal
  "SK", // Slovakia
  "SI", // Slovenia
  "ES", // Spain
]);

/**
 * Maps a visitor's country (ISO-3166 alpha-2) to the currency we display by
 * default. Unknown / null countries fall back to USD — the most broadly
 * understood currency for international travellers.
 *
 * - Morocco (MA) → MAD
 * - Eurozone → EUR
 * - United Kingdom (GB) → GBP
 * - everything else / unknown → USD
 */
export function currencyForCountry(country: string | null | undefined): CurrencyCode {
  const code = country?.trim().toUpperCase();
  if (!code) return "USD";
  if (code === "MA") return "MAD";
  if (code === "GB") return "GBP";
  if (EUROZONE_COUNTRIES.has(code)) return "EUR";
  return "USD";
}

/**
 * Fetches the visitor's country from the edge `/__geo` endpoint (served by
 * `server.ts`, never cached). Returns null on any failure or when running
 * outside Cloudflare (e.g. local dev), so callers degrade gracefully.
 */
export async function fetchGeoCountry(signal?: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch("/__geo", { signal, headers: { accept: "application/json" } });
    if (!res.ok) return null;
    const data = (await res.json()) as { country?: unknown };
    return typeof data.country === "string" && data.country ? data.country : null;
  } catch {
    return null;
  }
}
